import React from 'react';
import { createRoot } from 'react-dom/client';
import { Stage, Layer, Image as KonvaImage, Rect, Group, Circle, Ellipse } from 'react-konva';
import './style.css';

// WordPress i18n (JSON translations via wp_set_script_translations).
declare global {
	interface Window {
		wp?: any;
	}
}

const __ = (text: string): string => {
	try {
		return (window as any)?.wp?.i18n?.__(text, 'khl-print-configurator') || text;
	} catch {
		return text;
	}
};

const sprintf = (format: string, ...args: any[]): string => {
	try {
		const fn = (window as any)?.wp?.i18n?.sprintf;
		if (typeof fn === 'function') return String(fn(format, ...args));
	} catch {
		// ignore
	}
	// Very small fallback: replace %s tokens left-to-right.
	let out = String(format);
	args.forEach((a) => {
		out = out.replace('%s', String(a));
	});
	return out;
};

declare global {
	interface Window {
		khlPrintCfg?: {
			restUrl: string;
			nonce: string;
			productId: number;
			minW: number;
			maxW: number;
			minH: number;
			maxH: number;
			tubeMaxCm?: number;
			minQty: number;
			qtyTiers?: Array<{ min: number; mult: number; setup?: number }>;
			tiers?: {
				upsellTemplate?: string;
				maxTemplate?: string;
			};
			minPrice?: number;
			priceM2?: number;
			templateId: number;
			templateConfigUrl?: string;
			production?: {
				laminatedNote?: string;
				options?: Record<string, { title?: string; text?: string; surchargePercent?: number; tooltip?: string }>;
			};
		};
	}
}

declare global {
	// Loaded via CDN (UMD) by the WP PHP enqueue.
	// eslint-disable-next-line no-var
	var tippy: any;
}

function useHtmlImage(url: string | null) {
	const [image, setImage] = React.useState<HTMLImageElement | null>(null);
	const [loaded, setLoaded] = React.useState(false);
	React.useEffect(() => {
		if (!url) {
			setImage(null);
			setLoaded(false);
			return;
		}
		const img = new window.Image();
		img.crossOrigin = 'anonymous';
		img.onload = () => {
			setImage(img);
			setLoaded(true);
		};
		img.onerror = () => {
			setImage(null);
			setLoaded(false);
		};
		img.src = url;
	}, [url]);
	return { image, loaded };
}

function apiUrl(base: string, path: string) {
	return String(base).replace(/\/+$/, '') + '/' + String(path).replace(/^\/+/, '');
}

async function postJson<T>(url: string, nonce: string, data: unknown): Promise<T> {
	const res = await fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': nonce,
		},
		body: JSON.stringify(data),
	});
	return res.json();
}

type PriceResponse = { unitPrice: number; totalPrice: number; currency: string };

type ProductionOptionKey = 'standard' | 'priority' | 'rush';
type ProductionOption = { key: ProductionOptionKey; title: string; text: string; surchargePercent: number; tooltip?: string };

type DeliveryOptionKey = string;
type DeliveryOption = { key: DeliveryOptionKey; title: string; tooltip?: string };

type TemplateMaterial = { id: number; title: string; minPrice: number; priceM2: number };
type TemplateMaterialWithTooltip = TemplateMaterial & { tooltip?: string; hide_finishes?: boolean };
type TemplateFinish = { id: number; title: string; enabled: boolean; minW: number; minH: number; priceM2: number; tooltip?: string };
type TemplateMockup = {
	id: number;
	title: string;
	type: string;
	imageUrl: string;
	imageW: number;
	imageH: number;
	placement: { x: number; y: number; w: number; h: number };
	tooltip?: string;
};

type TemplateConfig = {
	id: number;
	title: string;
	defaultMaterialId: number;
	defaultFinishId?: number;
	showNoneFinish?: boolean;
	noneFinishTitle?: string;
	noneFinishTooltip?: string;
	defaultDeliveryKey?: string;
	materials: TemplateMaterialWithTooltip[];
	finishes?: TemplateFinish[];
	deliveryOptions?: Array<{ key: string; title: string; tooltip?: string }>;
	mockups?: TemplateMockup[];
};

function TooltipIcon({ content, corner, onPointerDown }: { content?: string; corner?: boolean; onPointerDown?: (e: any) => void }) {
	const c = String(content || '').trim();
	if (!c) return null;
	return (
		<span
			className={`khlp__tipIcon khlp-tooltip ${corner ? 'khlp__tipIcon--corner' : ''}`}
			data-tippy-content={c}
			role="button"
			tabIndex={0}
			aria-label={__('Info')}
			onPointerDown={(e) => {
				e.stopPropagation();
				onPointerDown?.(e);
			}}
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
			}}
			onKeyDown={(e) => {
				// Avoid selecting the item when pressing Enter/Space on the icon.
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					e.stopPropagation();
				}
			}}
		>
			?
		</span>
	);
}

type UploadResponse = {
	url: string;
	mime: string;
	previewUrl?: string;
	previewMime?: string;
	sizeBytes?: number;
	widthPx?: number;
	heightPx?: number;
};

function formatBytes(n: number) {
	if (!Number.isFinite(n) || n <= 0) return '—';
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
	return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function formatMoney(amount: number, currency: string) {
	const n = Number(amount);
	if (!Number.isFinite(n)) return '—';
	// Prefer Intl if available.
	try {
		if (typeof Intl !== 'undefined' && (Intl as any).NumberFormat) {
			// We only have a currency symbol from backend, so use EUR as default.
			const iso = currency === '€' ? 'EUR' : 'EUR';
			return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: iso, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
		}
	} catch {
		// ignore
	}
	const val = n.toFixed(2).replace('.', ',');
	return `${currency} ${val}`;
}

function computeDpi(widthPx: number, heightPx: number, widthCm: number, heightCm: number) {
	const cmToIn = (cm: number) => cm / 2.54;
	const wIn = widthCm > 0 ? cmToIn(widthCm) : 0;
	const hIn = heightCm > 0 ? cmToIn(heightCm) : 0;
	const dpiW = wIn > 0 ? widthPx / wIn : 0;
	const dpiH = hIn > 0 ? heightPx / hIn : 0;
	const dpi = dpiW && dpiH ? Math.min(dpiW, dpiH) : Math.max(dpiW, dpiH);
	return {
		dpi: Number.isFinite(dpi) ? dpi : 0,
		dpiW: Number.isFinite(dpiW) ? dpiW : 0,
		dpiH: Number.isFinite(dpiH) ? dpiH : 0,
	};
}

function qualityFromDpi(dpi: number): { level: 'good' | 'ok' | 'bad'; message: string } {
	// Practical print thresholds for stickers.
	if (!Number.isFinite(dpi) || dpi <= 0) return { level: 'ok', message: __('Resolutie onbekend.') };
	if (dpi >= 250) return { level: 'good', message: __('Perfecte printkwaliteit.') };
	if (dpi >= 150) return { level: 'ok', message: __('Goede kwaliteit. Kan licht softer worden bij heel grote formaten.') };
	return { level: 'bad', message: __('Resolutie te laag. Deze afbeelding kan wazig worden bij groot formaat.') };
}

function clamp(n: number, min: number, max: number) {
	if (Number.isNaN(n)) return min;
	return Math.max(min, Math.min(max, n));
}

function clampRange(n: number, a: number, b: number) {
	const min = Math.min(a, b);
	const max = Math.max(a, b);
	return clamp(n, min, max);
}

function normalizeDeg180(deg: number) {
	let d = deg;
	while (d > 180) d -= 360;
	while (d <= -180) d += 360;
	return d;
}

function App() {
	const cfgMaybe = window.khlPrintCfg;
	if (!cfgMaybe) return null;
	const cfg = cfgMaybe;

	const [uploadUrl, setUploadUrl] = React.useState<string | null>(null);
	const [uploadMime, setUploadMime] = React.useState<string>('');
	const [uploadPreviewUrl, setUploadPreviewUrl] = React.useState<string>('');
	const [uploadPreviewMime, setUploadPreviewMime] = React.useState<string>('');
	const [uploadFileName, setUploadFileName] = React.useState<string>('');
	const minW = cfg.minW && cfg.minW > 0 ? cfg.minW : 1;
	const minH = cfg.minH && cfg.minH > 0 ? cfg.minH : 1;
	const maxW = cfg.maxW && cfg.maxW > 0 ? cfg.maxW : 9999;
	const maxH = cfg.maxH && cfg.maxH > 0 ? cfg.maxH : 9999;
	// UX: allow free typing (e.g. type "15" even if min=2) by using string inputs,
	// then clamp to min/max on blur / Enter.
	const [widthInput, setWidthInput] = React.useState<string>(String(clamp(10, minW, maxW)));
	const [heightInput, setHeightInput] = React.useState<string>(String(clamp(10, minH, maxH)));
	const widthCm = (() => {
		const n = parseFloat(widthInput);
		return Number.isFinite(n) ? n : 0;
	})();
	const heightCm = (() => {
		const n = parseFloat(heightInput);
		return Number.isFinite(n) ? n : 0;
	})();
	const [qty, setQty] = React.useState<number>(Math.max(1, cfg.minQty || 1));
	// Keep WooCommerce quantity in sync so cart totals reflect the configurator qty.
	React.useEffect(() => {
		const input = document.querySelector('form.cart input.qty') as HTMLInputElement | null;
		if (!input) return;
		input.value = String(qty);
		input.setAttribute('value', String(qty));
	}, [qty]);
	const [price, setPrice] = React.useState<PriceResponse | null>(null);
	const [isPricing, setIsPricing] = React.useState<boolean>(false);
	const [status, setStatus] = React.useState<string>('');
	const [errors, setErrors] = React.useState<string[]>([]);
	const [tubeError, setTubeError] = React.useState<string>('');
	const [imgPos, setImgPos] = React.useState<{ x: number; y: number }>({ x: 60, y: 60 });
	const [templateCfg, setTemplateCfg] = React.useState<TemplateConfig | null>(null);
	const [materialId, setMaterialId] = React.useState<number>(0);
	const [finishId, setFinishId] = React.useState<number>(0);
	const [deliveryKey, setDeliveryKey] = React.useState<DeliveryOptionKey>('sheet_kisscut');
	const [materialTouched, setMaterialTouched] = React.useState<boolean>(false);
	const [finishTouched, setFinishTouched] = React.useState<boolean>(false);
	const [isDrag, setIsDrag] = React.useState(false);
	const [productionKey, setProductionKey] = React.useState<ProductionOptionKey>('standard');
	const [uploadMeta, setUploadMeta] = React.useState<{ sizeBytes: number; widthPx: number; heightPx: number } | null>(null);
	// Cutline is now handled purely server-side (export) and/or by standard shapes.
	// Background mode for the preview stage.
	// Fixed to white (customer doesn't need to change this).
	const previewMode: 'white' = 'white';
	// We keep internal state but we will show everything visible like fixjesticker.
	const [activeTab, setActiveTab] = React.useState<'upload' | 'material' | 'shape' | 'size' | 'order'>('upload');
	const [mockupId, setMockupId] = React.useState<number>(0);
	const [stickerScale, setStickerScale] = React.useState<number>(1);
	const [stickerRotation, setStickerRotation] = React.useState<number>(0);
	const [lockRatio, setLockRatio] = React.useState<boolean>(true);
	const lastEditedRef = React.useRef<'w' | 'h' | null>(null);

	// Initialize Tippy tooltips (loaded globally via CDN).
	// NOTE: we must re-run when parts of the UI mount/unmount (e.g. finishes hidden/shown)
	// otherwise newly rendered TooltipIcon elements won't get a tippy instance.
	React.useEffect(() => {
		const w = window as any;
		const t = w?.tippy;
		if (typeof t !== 'function') return;

		const raf = window.requestAnimationFrame(() => {
			const els = Array.from(document.querySelectorAll('.khlp-tooltip')) as any[];
			els.forEach((el) => {
				if (!el) return;
				const content = String(el.getAttribute('data-tippy-content') || '').trim();
				if (!content) return;
				// Prevent double-init.
				if ((el as any)._tippy) return;
				t(el, { content, placement: 'top', theme: 'light', arrow: true });
			});
		});

		return () => window.cancelAnimationFrame(raf);
		// We include material/finish deps so tooltips are re-attached when that section is toggled.
	}, [templateCfg, widthCm, heightCm, materialId, finishId]);

	function selectShape(nextMockupId: number, nextShapeType: string) {
		setMockupId(nextMockupId);

		// Round / square must always keep 1:1 ratio.
		if (nextShapeType === 'rond' || nextShapeType === 'vierkant' || nextShapeType === 'vierkant_rounded') {
			setLockRatio(true);
			// Normalize current size to 1:1 (keep width, adjust height).
			const w = clamp(widthCm, minW, maxW);
			const h = clamp(w, minH, maxH);
			setWidthInput(String(w));
			setHeightInput(String(h));
			lastEditedRef.current = 'h';
			return;
		}

		// UX: if customer starts from a square size (default 10x10), picking an oval/rectangle
		// should immediately *look* like that shape. We only auto-adjust if the current ratio
		// is ~1 (square), so we don't fight the user's custom size.
		const isSquare = widthCm > 0 && heightCm > 0 && Math.abs(widthCm / heightCm - 1) < 0.06;
		if (!isSquare) return;

		// Reasonable defaults; customer can still edit afterwards.
		if (nextShapeType === 'ovaal_h') {
			const w = clamp(widthCm, minW, maxW);
			const h = clamp(w * 0.7, minH, maxH);
			setWidthInput(String(w));
			setHeightInput(String(h));
			lastEditedRef.current = 'h';
			setLockRatio(false);
			return;
		}
		if (nextShapeType === 'rechthoek' || nextShapeType === 'rechthoek_rounded') {
			const w = clamp(widthCm, minW, maxW);
			const h = clamp(w * 0.6, minH, maxH);
			setWidthInput(String(w));
			setHeightInput(String(h));
			lastEditedRef.current = 'h';
			setLockRatio(false);
			return;
		}
	}

	// For PDF we prefer a server-generated PNG preview when available.
	const displayUrl = uploadPreviewUrl || uploadUrl;
	const { image, loaded: imageLoaded } = useHtmlImage(displayUrl);
	const stageRef = React.useRef<any>(null);
	const leftColRef = React.useRef<HTMLDivElement | null>(null);
	const rightColRef = React.useRef<HTMLDivElement | null>(null);
	const stickyRef = React.useRef<HTMLDivElement | null>(null);
	const stickyPlaceholderRef = React.useRef<HTMLDivElement | null>(null);

	// Sticky fallback for themes (e.g. Flatsome layouts) that break CSS position:sticky
	// due to overflow/transform on ancestors.
	React.useEffect(() => {
		let raf = 0;
		const onFrame = () => {
			raf = 0;
			const stickyEl = stickyRef.current;
			const leftEl = leftColRef.current;
			const rightEl = rightColRef.current;
			const ph = stickyPlaceholderRef.current;
			if (!stickyEl || !leftEl || !rightEl || !ph) return;
			const isDesktop = window.innerWidth >= 768;
			if (!isDesktop) {
				// Reset styles.
				stickyEl.style.position = '';
				stickyEl.style.top = '';
				stickyEl.style.width = '';
				stickyEl.style.left = '';
				stickyEl.style.right = '';
				stickyEl.style.bottom = '';
				ph.style.height = '0px';
				return;
			}

			const topGap = 20;
			const stickyRect = stickyEl.getBoundingClientRect();
			const stickyH = stickyRect.height;
			const leftRect = leftEl.getBoundingClientRect();
			const rightRect = rightEl.getBoundingClientRect();
			const leftTop = leftRect.top + window.scrollY;
			const rightBottom = rightRect.bottom + window.scrollY;
			const startY = leftTop - topGap;
			const stopY = rightBottom - stickyH - topGap;
			const y = window.scrollY;

			// Keep layout stable when we switch to fixed/absolute.
			ph.style.height = `${stickyH}px`;

			if (y < startY) {
				// Normal flow.
				stickyEl.style.position = '';
				stickyEl.style.top = '';
				stickyEl.style.width = '';
				stickyEl.style.left = '';
				stickyEl.style.bottom = '';
				ph.style.height = '0px';
				return;
			}

			if (y >= stopY) {
				// Stick to bottom of the right column.
				const top = Math.max(0, rightBottom - stickyH - leftTop - topGap);
				stickyEl.style.position = 'absolute';
				stickyEl.style.top = `${top}px`;
				stickyEl.style.left = '0px';
				stickyEl.style.width = '100%';
				stickyEl.style.bottom = '';
				return;
			}

			// Fixed to viewport.
			stickyEl.style.position = 'fixed';
			stickyEl.style.top = `${topGap}px`;
			stickyEl.style.left = `${leftRect.left}px`;
			stickyEl.style.width = `${leftRect.width}px`;
			stickyEl.style.bottom = '';
		};

		const onScroll = () => {
			if (raf) return;
			raf = window.requestAnimationFrame(onFrame);
		};
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll);
		// initial
		onScroll();
		return () => {
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', onScroll);
			if (raf) window.cancelAnimationFrame(raf);
		};
	}, []);

	const STAGE_W = 420;
	const STAGE_H = 420;
	// Higher internal render resolution for sharper preview on retina screens.
	// Keeps the visual size (CSS pixels) the same.
	const STAGE_PIXEL_RATIO = 2;
	const PAD = 20;
	const box = React.useMemo(() => {
		const maxW = STAGE_W - PAD * 2;
		const maxH = STAGE_H - PAD * 2;
		const r = widthCm > 0 && heightCm > 0 ? widthCm / heightCm : 1;
		let w = maxW;
		let h = maxH;
		if (r >= 1) {
			w = maxW;
			h = maxW / r;
			if (h > maxH) {
				h = maxH;
				w = maxH * r;
			}
		} else {
			h = maxH;
			w = maxH * r;
			if (w > maxW) {
				w = maxW;
				h = maxW / r;
			}
		}
		const x = Math.round((STAGE_W - w) / 2);
		const y = Math.round((STAGE_H - h) / 2);
		return { x, y, w, h };
	}, [heightCm, widthCm]);

	const shapeMockups = React.useMemo(() => {
		const all = templateCfg?.mockups || [];
		// Limit to the shapes you requested (including rounded corners variants).
		const allowed = new Set([
			'vorm_vierkant',
			'vorm_vierkant_rounded',
			'vorm_rechthoek',
			'vorm_rechthoek_rounded',
			'vorm_rond',
			'vorm_ovaal_h',
			'vorm_contour',
		]);
		return all.filter((m) => allowed.has(String(m.type || '')));
	}, [templateCfg?.mockups]);

	const showNoneFinish = Boolean((templateCfg as any)?.showNoneFinish);
	const noneFinishTitle = String((templateCfg as any)?.noneFinishTitle || __('Geen'));
	const noneFinishTooltip = String((templateCfg as any)?.noneFinishTooltip || '');

	const visibleFinishes = React.useMemo(() => {
		const all = Array.isArray(templateCfg?.finishes) ? templateCfg!.finishes! : [];
		const noneTitleNorm = noneFinishTitle.trim().toLowerCase();
		return all
			.filter((f) => f && f.enabled)
			// Prevent duplicates if someone also created a finish with exactly the same label as the “none” option.
			.filter((f) => {
				if (!showNoneFinish) return true;
				const t = String((f as any)?.title || '').trim().toLowerCase();
				return t !== noneTitleNorm;
			})
			.filter((f) => (f.minW ? widthCm >= f.minW : true) && (f.minH ? heightCm >= f.minH : true));
	}, [noneFinishTitle, showNoneFinish, templateCfg, widthCm, heightCm]);

	const selectedMaterial = React.useMemo(() => {
		const id = Number(materialId || templateCfg?.defaultMaterialId || 0);
		return templateCfg?.materials?.find((m) => Number(m.id) === id) || null;
	}, [materialId, templateCfg?.defaultMaterialId, templateCfg?.materials]);

	const hideFinishesForMaterial = Boolean((selectedMaterial as any)?.hide_finishes);
	// When a material says "hide finishes", force finishId to 0 so pricing stays consistent.
	React.useEffect(() => {
		if (!hideFinishesForMaterial) return;
		if ((finishId || 0) !== 0) setFinishId(0);
	}, [finishId, hideFinishesForMaterial]);

	// If template says “none” is not allowed, we should ensure a real finish is selected.
	React.useEffect(() => {
		if (!templateCfg) return;
		if (hideFinishesForMaterial) return; // material rule already forces 0
		if (showNoneFinish) return;
		if ((finishId || 0) !== 0) return;
		// Pick template default finish if present, else first visible finish.
		const def = Number((templateCfg as any)?.defaultFinishId || 0);
		const defOk = def > 0 && visibleFinishes.some((f) => f.id === def);
		const fallback = visibleFinishes[0]?.id || 0;
		const next = defOk ? def : fallback;
		if (next > 0) setFinishId(next);
	}, [finishId, hideFinishesForMaterial, showNoneFinish, templateCfg, visibleFinishes]);

	const hasLamination = Boolean(finishId && finishId > 0);
	const laminatedNote = String(cfg.production?.laminatedNote || __('Gelamineerde solvent prints hebben extra droogtijd nodig.'));
	const productionOptions = React.useMemo<ProductionOption[]>(() => {
		const defaults: Record<ProductionOptionKey, ProductionOption> = {
			standard: { key: 'standard', title: __('Standaard levering'), text: __('3–5 werkdagen'), surchargePercent: 0 },
			priority: { key: 'priority', title: __('Prioriteit productie'), text: __('1–2 werkdagen'), surchargePercent: 20 },
			rush: { key: 'rush', title: __('Spoed productie'), text: __('Binnen 24u geproduceerd'), surchargePercent: 50 },
		};
		const opts = (cfg.production?.options || {}) as any;
		const merged: ProductionOption[] = (['standard', 'priority', 'rush'] as ProductionOptionKey[]).map((k) => {
			const o = opts?.[k] || {};
			return {
				key: k,
				title: String(o.title || defaults[k].title),
				text: String(o.text || defaults[k].text),
				surchargePercent: Number.isFinite(Number(o.surchargePercent)) ? Number(o.surchargePercent) : defaults[k].surchargePercent,
				tooltip: String(o.tooltip || ''),
			};
		});
		return hasLamination ? merged.filter((o) => o.key !== 'rush') : merged;
	}, [cfg.production?.options, hasLamination]);

	/**
	 * Delivery format ("Levering als")
	 * Priority:
	 * 1) templateCfg.deliveryOptions (template-specific allowed options)
	 * 2) window.khlPrintCfg.deliveryOptions (global admin list)
	 * 3) fallback defaults
	 */
	const deliveryOptions = React.useMemo<DeliveryOption[]>(() => {
		const defaults: DeliveryOption[] = [
			{
				key: 'sheet_kisscut',
				title: 'OP VEL (KISS-CUT)',
				tooltip: 'Meerdere stickers op een handzaam vel. Ideaal voor snel plakken of productetiketten.',
			},
			{
				key: 'loose_diecut',
				title: 'LOSSE STICKERS (DIE-CUT)',
				tooltip: 'Elke sticker volledig individueel uitgesneden inclusief het achtervel. Perfect om uit te delen!',
			},
		];

		const tplOptions = (templateCfg as any)?.deliveryOptions;
		if (Array.isArray(tplOptions) && tplOptions.length) {
			return tplOptions
				.map((o: any) => ({ key: String(o?.key || ''), title: String(o?.title || ''), tooltip: String(o?.tooltip || '') }))
				.filter((o: any) => o.key && o.title);
		}

		const cfgOptions = (cfg as any)?.deliveryOptions;
		if (Array.isArray(cfgOptions) && cfgOptions.length) {
			return cfgOptions
				.map((o: any) => ({ key: String(o?.key || ''), title: String(o?.title || ''), tooltip: String(o?.tooltip || '') }))
				.filter((o: any) => o.key && o.title);
		}
		return defaults;
	}, [cfg, templateCfg]);

	// Ensure we always have a valid deliveryKey.
	// Prefer template default if provided.
	React.useEffect(() => {
		if (!deliveryOptions.length) return;
		const tplDefault = String((templateCfg as any)?.defaultDeliveryKey || '').trim();
		const desired = tplDefault && deliveryOptions.some((o) => o.key === tplDefault) ? tplDefault : '';
		const ok = deliveryOptions.some((o) => o.key === deliveryKey);
		if (!deliveryKey || !ok) {
			setDeliveryKey(desired || deliveryOptions[0]!.key);
		}
	}, [deliveryKey, deliveryOptions, templateCfg]);

	// If lamination is selected, rush is not allowed.
	React.useEffect(() => {
		if (hasLamination && productionKey === 'rush') {
			setProductionKey('priority');
		}
	}, [hasLamination, productionKey]);

	// If current finish is no longer available for current dimensions, reset.
	React.useEffect(() => {
		if (!finishId) return;
		const ok = visibleFinishes.some((f) => f.id === finishId);
		if (!ok) {
			// If none option is allowed: go to 0. Otherwise choose the first available finish.
			setFinishId(showNoneFinish ? 0 : Number(visibleFinishes[0]?.id || 0));
		}
	}, [finishId, showNoneFinish, visibleFinishes]);

	const selectedMockup = React.useMemo(() => {
		if (!shapeMockups.length) return null;
		const id = mockupId || shapeMockups[0]?.id || 0;
		return shapeMockups.find((m) => m.id === id) || shapeMockups[0] || null;
	}, [mockupId, shapeMockups]);

	const isShapeMockup = Boolean(selectedMockup);
	const shapeType = (selectedMockup?.type || '').replace('vorm_', '');
	const targetRatio = React.useMemo(() => {
		// Optional: only enforce for strict shapes (square/circle).
		switch (shapeType) {
			case 'vierkant':
			case 'vierkant_rounded':
			case 'rond':
				return 1;
			default:
				return null;
		}
	}, [shapeType]);

	// When shape changes and ratio is locked, normalize the current size to the new ratio.
	React.useEffect(() => {
		if (!lockRatio) return;
		if (!targetRatio) return;
		if (!widthCm || widthCm <= 0) return;
		const nextH = clamp(widthCm / targetRatio, minH, maxH);
		setHeightInput(String(nextH));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lockRatio, targetRatio]);
	const shapeBox = React.useMemo(() => {
		// Where the shape cutout lives within the stage.
		// IMPORTANT UX: The shape must be visually centered in the preview (DESK1 request).
		// We therefore always use the centered, padded fit-box based on the requested
		// width/height ratio. Mockup placement is not used for positioning.
		return box;
	}, [box]);

	const imageRender = React.useMemo(() => {
		if (!image) return null;
		const fitBox = shapeBox;
		const scaleToFit = Math.min(fitBox.w / image.width, fitBox.h / image.height);
		const scale = scaleToFit * stickerScale;
		const w = Math.round(image.width * scale);
		const h = Math.round(image.height * scale);
		return { w, h };
	}, [image, shapeBox, stickerScale]);

	const exportMapping = React.useMemo(() => {
		if (!imageRender) return null;
		return {
			stageW: STAGE_W,
			stageH: STAGE_H,
			box: { x: shapeBox.x, y: shapeBox.y, w: shapeBox.w, h: shapeBox.h },
			image: {
				// imageRender is the displayed image bounding box in stage px
				scaleX: imageRender.w,
				scaleY: imageRender.h,
				centerX: imgPos.x + imageRender.w / 2,
				centerY: imgPos.y + imageRender.h / 2,
				offsetX: 0.5,
				offsetY: 0.5,
				rotation: stickerRotation,
			},
		};
	}, [STAGE_H, STAGE_W, imageRender, imgPos.x, imgPos.y, shapeBox.h, shapeBox.w, shapeBox.x, shapeBox.y, stickerRotation]);

	const boundCenterPos = React.useCallback(
		(pos: { x: number; y: number }) => {
			if (!imageRender) return pos;
			const box = shapeBox;
			const w = imageRender.w;
			const h = imageRender.h;

			const range = (start: number, size: number, imgSize: number) => {
				const half = imgSize / 2;
				// If the image is larger than the box, we must keep the box *inside* the image.
				// If the image is smaller, we keep the image inside the box.
				if (imgSize >= size) {
					return { min: start + size - half, max: start + half };
				}
				return { min: start + half, max: start + size - half };
			};

			const xr = range(box.x, box.w, w);
			const yr = range(box.y, box.h, h);
			return {
				x: clampRange(pos.x, xr.min, xr.max),
				y: clampRange(pos.y, yr.min, yr.max),
			};
		},
		[imageRender, shapeBox]
	);

	function nudge(dx: number, dy: number) {
		if (!imageRender) return;
		const cx = imgPos.x + imageRender.w / 2 + dx;
		const cy = imgPos.y + imageRender.h / 2 + dy;
		const bounded = boundCenterPos({ x: cx, y: cy });
		setImgPos({ x: bounded.x - imageRender.w / 2, y: bounded.y - imageRender.h / 2 });
	}

	// If the customer changes width/height (or scale/shape changes), keep the image position valid.
	React.useEffect(() => {
		if (!imageRender) return;
		const cx = imgPos.x + imageRender.w / 2;
		const cy = imgPos.y + imageRender.h / 2;
		const bounded = boundCenterPos({ x: cx, y: cy });
		const next = { x: bounded.x - imageRender.w / 2, y: bounded.y - imageRender.h / 2 };
		const dx = Math.abs(next.x - imgPos.x);
		const dy = Math.abs(next.y - imgPos.y);
		if (dx > 0.5 || dy > 0.5) {
			setImgPos(next);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [boundCenterPos, imageRender?.w, imageRender?.h, shapeBox.x, shapeBox.y, shapeBox.w, shapeBox.h]);

	const shapeClipFunc = React.useMemo(() => {
		if (!isShapeMockup) return undefined;
		const x = shapeBox.x;
		const y = shapeBox.y;
		const w = shapeBox.w;
		const h = shapeBox.h;
		const cx = x + w / 2;
		const cy = y + h / 2;
		const rx = w / 2;
		const ry = h / 2;
		const r = Math.min(rx, ry);

		const roundedRect = (ctx: any, x0: number, y0: number, w0: number, h0: number, rad: number) => {
			const rr = Math.max(0, Math.min(rad, Math.min(w0, h0) / 2));
			ctx.beginPath();
			ctx.moveTo(x0 + rr, y0);
			ctx.arcTo(x0 + w0, y0, x0 + w0, y0 + h0, rr);
			ctx.arcTo(x0 + w0, y0 + h0, x0, y0 + h0, rr);
			ctx.arcTo(x0, y0 + h0, x0, y0, rr);
			ctx.arcTo(x0, y0, x0 + w0, y0, rr);
			ctx.closePath();
		};

		return (ctx: any) => {
			switch (shapeType) {
				case 'rond':
					ctx.beginPath();
					ctx.arc(cx, cy, r, 0, Math.PI * 2, false);
					ctx.closePath();
					break;
				case 'ovaal_h':
				case 'ovaal_v':
					ctx.beginPath();
					if (typeof ctx.ellipse === 'function') {
						ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
					} else {
						// Fallback: approximate oval as rounded rect.
						roundedRect(ctx, x, y, w, h, Math.min(rx, ry));
					}
					ctx.closePath();
					break;
				case 'vierkant_rounded':
				case 'rechthoek_rounded':
				case 'rounded':
					roundedRect(ctx, x, y, w, h, 18);
					break;
				case 'vierkant':
				case 'rechthoek':
				default:
					ctx.beginPath();
					ctx.rect(x, y, w, h);
					ctx.closePath();
			}
		};
	}, [isShapeMockup, shapeBox.h, shapeBox.w, shapeBox.x, shapeBox.y, shapeType]);

	// Center image when a new file is uploaded.
	React.useEffect(() => {
		if (!imageLoaded || !imageRender) return;
		setImgPos({ x: shapeBox.x + (shapeBox.w - imageRender.w) / 2, y: shapeBox.y + (shapeBox.h - imageRender.h) / 2 });
	}, [imageLoaded, imageRender, shapeBox.h, shapeBox.w, shapeBox.x, shapeBox.y]);

	// Load template config (materials) if available.
	React.useEffect(() => {
		let cancelled = false;
		const url = cfg.templateConfigUrl || '';
		if (!url) {
			setTemplateCfg(null);
			return;
		}
		fetch(url)
			.then((r) => r.json())
			.then((json: TemplateConfig) => {
				if (cancelled) return;
				if (json && Array.isArray(json.materials)) {
					setTemplateCfg(json);
					// Default selection (only if nothing selected yet)
					setMaterialId((prev) => prev || Number(json.defaultMaterialId || json.materials?.[0]?.id || 0));
					// IMPORTANT: only auto-select finish if template has an explicit defaultFinishId.
					// Otherwise keep it at 0 (= Geen) so the customer doesn't get a surprise add-on.
					setFinishId((prev) => prev || Number((json as any).defaultFinishId || 0));
						if (Array.isArray(json.mockups) && json.mockups.length) {
							// Default to first allowed shape mockup.
							const allowed = new Set([
								'vorm_vierkant',
								'vorm_vierkant_rounded',
								'vorm_rechthoek',
								'vorm_rechthoek_rounded',
								'vorm_rond',
								'vorm_ovaal_h',
								'vorm_contour',
							]);
							const firstShape = (json.mockups || []).find((m) => allowed.has(String((m as any)?.type || ''))) as any;
							setMockupId((prev) => prev || Number(firstShape?.id || 0));
						}
				}
			})
			.catch(() => {
				if (cancelled) return;
				setTemplateCfg(null);
			});
		return () => {
			cancelled = true;
		};
	}, [cfg.templateConfigUrl]);

	function updateHiddenFields(previewOverride?: string) {
		const materialHidden = document.getElementById('khl_print_material_id') as HTMLInputElement | null;
		if (materialHidden) {
			materialHidden.value = materialId ? String(materialId) : '';
		}
		const hidden = document.getElementById('khl_print_config_json') as HTMLInputElement | null;
		if (hidden) {
			hidden.value = JSON.stringify({
				uploadUrl,
				uploadMime,
				uploadPreviewUrl,
				uploadPreviewMime,
				exportMapping,
				materialId,
				finishId,
				deliveryKey,
				productionKey,
				shapeType,
				previewMode,
				mockupId,
				lockRatio,
				transform: { scale: stickerScale, rotation: stickerRotation },
				widthCm,
				heightCm,
				qty,
				templateId: cfg.templateId,
				img: { x: imgPos.x, y: imgPos.y },
			});
		}
		const previewHidden = document.getElementById('khl_print_preview_b64') as HTMLInputElement | null;
		if (previewHidden) {
			previewHidden.value = previewOverride ?? previewHidden.value ?? '';
		}
	}



	function computePreview(): string {
		const stage = stageRef.current;
		if (!stage || typeof stage.toDataURL !== 'function') return '';
		try {
			const dataUrl = String(
				stage.toDataURL({
					pixelRatio: STAGE_PIXEL_RATIO,
					mimeType: 'image/jpeg',
					quality: 0.9,
				})
			);
			return dataUrl.startsWith('data:image') && dataUrl.length > 50 ? dataUrl : '';
		} catch {
			return '';
		}
	}

	function commitWidth(next?: string) {
		const raw = next ?? widthInput;
		const n = Number(raw);
		const clamped = clamp(Number.isFinite(n) ? n : minW, minW, maxW);
		setWidthInput(String(clamped));
		if (lockRatio && targetRatio) {
			const nextH = clamp(clamped / targetRatio, minH, maxH);
			setHeightInput(String(nextH));
			lastEditedRef.current = 'h';
		}
	}

	function commitHeight(next?: string) {
		const raw = next ?? heightInput;
		const n = Number(raw);
		const clamped = clamp(Number.isFinite(n) ? n : minH, minH, maxH);
		setHeightInput(String(clamped));
		if (lockRatio && targetRatio) {
			const nextW = clamp(clamped * targetRatio, minW, maxW);
			setWidthInput(String(nextW));
			lastEditedRef.current = 'w';
		}
	}

	React.useEffect(() => {
		let cancelled = false;
		// Small debounce so price updates feel smooth while typing.
		const t = window.setTimeout(() => {
			if (cancelled) return;
			// If dimensions are incomplete, just clear the price.
			if (!Number.isFinite(widthCm) || !Number.isFinite(heightCm) || widthCm <= 0 || heightCm <= 0) {
				setPrice(null);
				return;
			}
			setIsPricing(true);
			setErrors([]);
			postJson<PriceResponse>(apiUrl(cfg.restUrl, 'price'), cfg.nonce, {
				productId: cfg.productId,
				materialId: materialId || undefined,
				// IMPORTANT: send 0 explicitly so backend does NOT apply a template default finish.
				finishId,
				mockupId: mockupId || undefined,
				productionKey,
				widthCm,
				heightCm,
				qty,
				minPrice: cfg.minPrice ?? undefined,
				materialPriceM2: cfg.priceM2 ?? undefined,
			})
				.then((p) => {
					if (cancelled) return;
					setPrice(p);
				})
				.catch(() => {
					if (cancelled) return;
					setPrice(null);
					setErrors([__('Kon prijs niet berekenen. Probeer opnieuw.')]);
				})
				.finally(() => {
					if (cancelled) return;
					setIsPricing(false);
				});
		}, 200);

		return () => {
			cancelled = true;
			window.clearTimeout(t);
		};
	}, [cfg.minPrice, cfg.nonce, cfg.priceM2, cfg.productId, cfg.restUrl, cfg.templateId, finishId, heightCm, materialId, mockupId, qty, widthCm, productionKey]);

	// Keep hidden JSON field in sync even if price call fails.
	React.useEffect(() => {
		updateHiddenFields();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uploadUrl, widthCm, heightCm, qty, imgPos.x, imgPos.y, materialId, finishId, deliveryKey, mockupId, previewMode, stickerRotation, stickerScale, lockRatio, exportMapping, materialTouched, finishTouched, productionKey]);

	// If we have a displayUrl but it doesn't load, show a helpful message.
	React.useEffect(() => {
		if (!displayUrl) return;
		if (imageLoaded) return;
		// Don't spam: only show for PDF.
		if (uploadMime === 'application/pdf') {
			if (!uploadPreviewUrl) {
					setErrors((prev) => (prev.length ? prev : [__('PDF preview is niet beschikbaar in de editor op deze server. Je PDF wordt wel meegestuurd in je bestelling.') ]));
			} else {
					setErrors((prev) => (prev.length ? prev : [__('Kon de gegenereerde PDF preview niet laden.') ]));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [displayUrl, imageLoaded, uploadMime, uploadPreviewUrl]);

		// Debounced preview generation.
	React.useEffect(() => {
		const t = window.setTimeout(() => {
			const p = computePreview();
			if (p) {
				const previewHidden = document.getElementById('khl_print_preview_b64') as HTMLInputElement | null;
				if (previewHidden) previewHidden.value = p;
			}
		}, 250);
		return () => window.clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uploadUrl, widthCm, heightCm, qty, imgPos.x, imgPos.y, imageLoaded, mockupId, previewMode, stickerRotation, stickerScale, lockRatio, productionKey, deliveryKey]);

	// Ensure latest preview is attached right before add-to-cart.
	React.useEffect(() => {
		const form = document.querySelector('form.cart');
		if (!form) return;
		const handler = () => {
			const p = computePreview();
			updateHiddenFields(p);
		};
		form.addEventListener('submit', handler, true);
		return () => form.removeEventListener('submit', handler, true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uploadUrl, widthCm, heightCm, qty, imgPos.x, imgPos.y, imageLoaded, mockupId, previewMode, stickerRotation, stickerScale, lockRatio, productionKey, deliveryKey]);

	async function onFile(file: File) {
		setErrors([]);
		setStatus(`${__('Uploaden')}: ${file.name} (${Math.round(file.size / 1024)} KB)`);
		setUploadFileName(file.name);
		setUploadMeta(null);
		// nothing
		const fd = new FormData();
		fd.append('file', file);
		const res = await fetch(apiUrl(cfg.restUrl, 'upload'), {
			method: 'POST',
			headers: { 'X-WP-Nonce': cfg.nonce },
			body: fd,
		});
	const json = (await res.json().catch(() => ({}))) as Partial<UploadResponse> & { message?: string };
		if (!res.ok) {
			setStatus(__('Upload mislukt'));
			setErrors([String(json?.message || __('Upload mislukt. Probeer een PNG/JPG/WEBP/PDF.'))]);
			return;
		}
		if (json?.url) {
			setUploadUrl(String(json.url));
			setUploadMime(String(json?.mime || ''));
			// nothing
			{
				const mime = String(json?.mime || '');
				const serverPrevUrl = String(json?.previewUrl || '');
				const serverPrevMime = String(json?.previewMime || '');
				// Default: server preview.
				setUploadPreviewUrl(serverPrevUrl);
				setUploadPreviewMime(serverPrevMime);
			}
			setUploadFileName((prev) => prev || String((file as any)?.name || ''));
			setUploadMeta({
				sizeBytes: Number(json?.sizeBytes || file.size || 0),
				widthPx: Number(json?.widthPx || 0),
				heightPx: Number(json?.heightPx || 0),
			});
			setStatus(__('Bestand geüpload.'));
			const mime = String(json?.mime || '');
			const pUrl = String(json?.previewUrl || '');
			if (mime === 'application/pdf' && !pUrl) {
				setErrors([__('PDF preview is niet beschikbaar in de editor op deze server. Je PDF wordt wel meegestuurd in je bestelling.')]);
			}

			// UX: go to the next step automatically.
			setActiveTab('material');
		} else {
			setStatus(__('Upload mislukt'));
			setErrors([__('Upload mislukt. Probeer een PNG/JPG/WEBP/PDF.')]);
		}
	}

	function scrollToSection(section: 'upload' | 'material' | 'shape' | 'size' | 'order') {
		setActiveTab(section);
		const el = document.getElementById(`khlp-section-${section}`);
		if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	const dpiInfo = uploadMeta ? computeDpi(uploadMeta.widthPx, uploadMeta.heightPx, widthCm, heightCm) : null;
	const quality = dpiInfo ? qualityFromDpi(dpiInfo.dpi) : null;

	const canOrder = Boolean(uploadUrl) && widthCm > 0 && heightCm > 0 && qty >= (cfg.minQty || 1);
	const tubeMax = Number((cfg as any).tubeMaxCm || 0);
	const tubeInvalid = tubeMax > 0 && widthCm > tubeMax && heightCm > tubeMax;
	React.useEffect(() => {
		if (!tubeInvalid) {
			setTubeError('');
			return;
		}
		const msg = __('Deze afmeting past niet in onze verzendkoker. Minstens één zijde (breedte of hoogte) moet maximaal %s cm zijn.');
		setTubeError(sprintf(msg, tubeMax));
	}, [tubeInvalid, tubeMax]);
	const canOrderWithTube = canOrder && !tubeInvalid;
	const qtyTiers = Array.isArray(cfg.qtyTiers) ? cfg.qtyTiers : [];
	const tiers = React.useMemo(() => {
		return qtyTiers
			.slice()
			.sort((a, b) => (a.min || 0) - (b.min || 0))
			.filter((t) => t && Number(t.min) > 0 && Number(t.mult) > 0);
	}, [qtyTiers]);
	const activeTierIdx = React.useMemo(() => {
		let idx = -1;
		for (let i = 0; i < tiers.length; i++) {
			if ((tiers[i]?.min || 0) <= qty) idx = i;
		}
		return idx;
	}, [qty, tiers]);
	const nextTier = activeTierIdx >= 0 ? tiers[activeTierIdx + 1] : tiers[0];
	const minQty = cfg.minQty || 1;
	const maxDiscount = React.useMemo(() => {
		if (!tiers.length) return 0;
		const last = tiers[tiers.length - 1];
		return Math.round((1 - Number(last?.mult || 1)) * 100);
	}, [tiers]);
	const upsellTemplate = String((cfg as any)?.tiers?.upsellTemplate || '').trim();
	const maxTemplate = String((cfg as any)?.tiers?.maxTemplate || '').trim();
	const renderTemplate = React.useCallback(
		(tpl: string, vars: Record<string, string | number>) => {
			let out = String(tpl || '');
			Object.entries(vars).forEach(([k, v]) => {
				// replaceAll isn't available in older TS lib targets. Use split/join instead.
				out = out.split(`{${k}}`).join(String(v));
			});
			return out;
		},
		[]
	);

	// Compute per-tier unit prices using backend price logic.
	const [tierUnitPrices, setTierUnitPrices] = React.useState<Record<number, { unitPrice: number; currency: string }>>({});
	React.useEffect(() => {
		let cancelled = false;
		// We only show tiers when size is valid.
		if (!Number.isFinite(widthCm) || !Number.isFinite(heightCm) || widthCm <= 0 || heightCm <= 0) {
			setTierUnitPrices({});
			return;
		}
		if (!tiers.length) {
			setTierUnitPrices({});
			return;
		}
		// (Re)fetch prices for all tier mins.
		const mins = Array.from(new Set(tiers.map((t) => Number(t.min || 0)).filter((m) => m > 0)));
		Promise.all(
			mins.map(async (m) => {
				try {
					const p = await postJson<PriceResponse>(apiUrl(cfg.restUrl, 'price'), cfg.nonce, {
						productId: cfg.productId,
						materialId: materialId || undefined,
						finishId,
						mockupId: mockupId || undefined,
						productionKey,
						widthCm,
						heightCm,
						qty: m,
						minPrice: cfg.minPrice ?? undefined,
						materialPriceM2: cfg.priceM2 ?? undefined,
					});
					return [m, { unitPrice: Number(p?.unitPrice || 0), currency: String((p as any)?.currency || '€') }] as const;
				} catch {
					return [m, { unitPrice: 0, currency: '€' }] as const;
				}
			}),
		).then((pairs) => {
			if (cancelled) return;
			const next: Record<number, { unitPrice: number; currency: string }> = {};
			pairs.forEach(([m, v]) => {
				next[m] = v;
			});
			setTierUnitPrices(next);
		});
		return () => {
			cancelled = true;
		};
	}, [cfg.minPrice, cfg.nonce, cfg.priceM2, cfg.productId, cfg.restUrl, finishId, heightCm, materialId, mockupId, productionKey, tiers, widthCm]);

	const currentUnitPrice = React.useMemo(() => {
		// Use current live price if available.
		if (price && Number.isFinite(Number(price.unitPrice))) {
			return formatMoney(Number(price.unitPrice), String((price as any)?.currency || '€'));
		}
		// Otherwise fall back to tier price for the active tier.
		if (activeTierIdx >= 0) {
			const min = Number(tiers[activeTierIdx]?.min || 0);
			const p = tierUnitPrices[min];
			if (p && Number.isFinite(Number(p.unitPrice))) return formatMoney(Number(p.unitPrice), String(p.currency || '€'));
		}
		return '';
	}, [activeTierIdx, price, tiers, tierUnitPrices]);

	const nextUnitPrice = React.useMemo(() => {
		if (!nextTier) return '';
		const min = Number(nextTier.min || 0);
		const p = tierUnitPrices[min];
		if (!p || !Number.isFinite(Number(p.unitPrice))) return '';
		return formatMoney(Number(p.unitPrice), String(p.currency || '€'));
	}, [nextTier, tierUnitPrices]);
	const canEditImage = Boolean(image);

	return (
		<div className="khlp">
			<div className="khlp__grid">
				<div className="khlp__colLeft" ref={leftColRef}>
					<div className="khlp__stickyPlaceholder" ref={stickyPlaceholderRef} aria-hidden="true" />
					<div className="khlp__stickyDesktop" ref={stickyRef}>
						<div className="khlp__previewWrap">
							<div className="khlp__preview">
								<div className="khlp__previewStage">
									<Stage width={STAGE_W} height={STAGE_H} pixelRatio={STAGE_PIXEL_RATIO} ref={stageRef}>
										<Layer>
				{/* Stage background (fixed) */}
				<Rect x={0} y={0} width={STAGE_W} height={STAGE_H} fill="#f6f6f8" />

				{/* Sticker shadow (soft drop shadow like fixjesticker) */}
				{isShapeMockup ? (
					shapeType === 'rond' ? (
						<Circle
							x={shapeBox.x + shapeBox.w / 2}
							y={shapeBox.y + shapeBox.h / 2}
							radius={Math.min(shapeBox.w, shapeBox.h) / 2}
							fill="#ffffff"
							shadowColor="#000"
							shadowBlur={16}
							shadowOpacity={0.18}
							shadowOffset={{ x: 0, y: 6 }}
						/>
					) : shapeType === 'ovaal_h' || shapeType === 'ovaal_v' ? (
						<Ellipse
							x={shapeBox.x + shapeBox.w / 2}
							y={shapeBox.y + shapeBox.h / 2}
							radiusX={shapeBox.w / 2}
							radiusY={shapeBox.h / 2}
							fill="#ffffff"
							shadowColor="#000"
							shadowBlur={16}
							shadowOpacity={0.18}
							shadowOffset={{ x: 0, y: 6 }}
						/>
					) : (
						<Rect
							x={shapeBox.x}
							y={shapeBox.y}
							width={shapeBox.w}
							height={shapeBox.h}
							fill="#ffffff"
							cornerRadius={shapeType === 'vierkant_rounded' || shapeType === 'rechthoek_rounded' || shapeType === 'rounded' ? 18 : 0}
							shadowColor="#000"
							shadowBlur={16}
							shadowOpacity={0.18}
							shadowOffset={{ x: 0, y: 6 }}
						/>
					)
				) : null}

							{image && imageRender ? (
								<Group
									clipX={shapeBox.x}
									clipY={shapeBox.y}
									clipWidth={shapeBox.w}
									clipHeight={shapeBox.h}
									clipFunc={shapeClipFunc as any}
								>
									<KonvaImage
										image={image}
										x={imgPos.x + imageRender.w / 2}
										y={imgPos.y + imageRender.h / 2}
										width={imageRender.w}
										height={imageRender.h}
										offsetX={imageRender.w / 2}
										offsetY={imageRender.h / 2}
										rotation={stickerRotation}
										draggable
										dragBoundFunc={boundCenterPos}
										onDragEnd={(e) => {
											// Dragging uses the center anchor, translate back to top-left.
											setImgPos({ x: e.target.x() - imageRender.w / 2, y: e.target.y() - imageRender.h / 2 });
										}}
									/>

								</Group>
							) : null}
						</Layer>
					</Stage>
				</div>
				<div className="khlp__hint">{__('Je preview wordt automatisch bewaard in je winkelmandje en bestelling.')}</div>
				</div>
			</div>

						<div className="khlp__ctaBox">
							<div className="khlp__ctaPrice">
								<span className="khlp__ctaPriceLabel">{__('Actuele prijs')}</span>
								<strong className="khlp__ctaPriceValue">
									{isPricing ? __('Berekenen…') : price ? `${price.currency} ${price.totalPrice.toFixed(2).replace('.', ',')}` : '—'}
								</strong>
							</div>
							<button
								className="khlp__addToCartBtn"
								type="button"
								disabled={!canOrderWithTube}
								onClick={() => {
									let form = document.querySelector('form.cart') as HTMLFormElement | null;
									// Make sure hidden fields + latest preview are present.
									const p = computePreview();
									updateHiddenFields(p);

									// Some custom product templates/pagebuilders don't output the Woo add-to-cart form.
									// In that case we create a minimal POST form ourselves.
									if (!form) {
										try {
											const f = document.createElement('form');
											f.method = 'post';
											f.action = window.location.href;
											f.style.display = 'none';
											document.body.appendChild(f);

											const add = document.createElement('input');
												add.type = 'hidden';
												add.name = 'add-to-cart';
												add.value = String(cfg.productId);
												f.appendChild(add);

											const q = document.createElement('input');
												q.type = 'hidden';
												q.name = 'quantity';
												q.value = String(qty);
												f.appendChild(q);

											(['khl_print_config_json', 'khl_print_preview_b64', 'khl_print_material_id'] as const).forEach((id) => {
												const el = document.getElementById(id) as HTMLInputElement | null;
												if (!el) return;
												const inp = document.createElement('input');
												inp.type = 'hidden';
												inp.name = el.name || id;
												inp.value = String(el.value || '');
												f.appendChild(inp);
											});

											f.submit();
											return;
										} catch (e) {
											console.error('KHL Print: add-to-cart fallback failed', e);
											setErrors((prev) => (prev.length ? prev : [__('Kon winkelwagen niet openen. Vernieuw de pagina en probeer opnieuw.')]));
											return;
										}
									}

									// Robust submit: WooCommerce expects an `add-to-cart` field (normally provided by
									// the default submit button). Because we hide that button, we submit via a
									// temporary submitter with name/value.
									const submitter = document.createElement('button');
									submitter.type = 'submit';
									submitter.name = 'add-to-cart';
									submitter.value = String(cfg.productId);
									submitter.style.display = 'none';
									form.appendChild(submitter);
									const anyForm = form as any;
									if (typeof anyForm.requestSubmit === 'function') {
										anyForm.requestSubmit(submitter);
									} else {
										submitter.click();
									}
									submitter.remove();
								}}
							>
								{__('Toevoegen aan winkelwagen')}
							</button>
							<div className="khlp__ctaHint">
								{canOrderWithTube
									? __('Je kan nu bestellen.')
									: tubeInvalid
										? __('Pas je afmetingen aan om te bestellen.')
										: __('Upload een bestand en vul afmetingen in om te bestellen.')}
							</div>
						</div>
					</div>
				</div>

				<div className="khlp__colRight" ref={rightColRef}>
					<div className="khlp__panel">
					<div className="khlp__panelTitle">{__('Personaliseer je sticker')}</div>
				<div className="khlp__tabs" role="tablist" aria-label={__('Configurator stappen')}>
						<button type="button" className={`khlp__tab ${activeTab === 'upload' ? 'isActive' : ''}`} onClick={() => scrollToSection('upload')}>{__('Upload')}</button>
						<button type="button" className={`khlp__tab ${activeTab === 'material' ? 'isActive' : ''}`} onClick={() => scrollToSection('material')}>{__('Materiaal')}</button>
						<button type="button" className={`khlp__tab ${activeTab === 'shape' ? 'isActive' : ''}`} onClick={() => scrollToSection('shape')}>{__('Vorm')}</button>
						<button type="button" className={`khlp__tab ${activeTab === 'size' ? 'isActive' : ''}`} onClick={() => scrollToSection('size')}>{__('Afmeting')}</button>
						<button type="button" className={`khlp__tab ${activeTab === 'order' ? 'isActive' : ''}`} onClick={() => scrollToSection('order')}>{__('Bestellen')}</button>
					</div>

					{/* Everything visible (fixjesticker-style). Tabs are just shortcuts. */}
					<div className="khlp__section" id="khlp-section-upload">
					<div className="khlp__sectionTitle">1. {__('Upload')}</div>
					<div className="khlp__sectionSub">{__('Upload je bestand en positioneer het in de snijvorm.')}</div>
						<div
							className={`khlp__dropzone ${isDrag ? 'isDrag' : ''}`}
							onDragEnter={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setIsDrag(true);
							}}
							onDragOver={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setIsDrag(true);
							}}
							onDragLeave={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setIsDrag(false);
							}}
							onDrop={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setIsDrag(false);
								const f = e.dataTransfer.files?.[0];
								if (f) void onFile(f);
							}}
						>
						<div className="khlp__dropTitle">{__('Sleep je bestand hier, of kies een bestand')}</div>
					<div className="khlp__dropSub">{__('Ondersteund: PNG, JPG, WEBP, PDF')}</div>
							<div className="khlp__filePicker">
							<label className="khlp__fileBtn">
								{__('Bestand kiezen')}
									<input
										type="file"
										accept=".png,.jpg,.jpeg,.webp,.pdf"
										style={{ display: 'none' }}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
											const f = e.target.files?.[0];
											if (f) void onFile(f);
										}}
									/>
								</label>
							<div className="khlp__fileName">{uploadFileName ? uploadFileName : __('Nog geen bestand gekozen')}</div>
							</div>
							{uploadMeta ? (
								<div className="khlp__fileMeta">
									<div>
									<strong>{__('Bestand')}:</strong> {formatBytes(uploadMeta.sizeBytes)}
									</div>
									{uploadMeta.widthPx > 0 && uploadMeta.heightPx > 0 ? (
										<div>
										<strong>{__('Resolutie')}:</strong> {uploadMeta.widthPx} × {uploadMeta.heightPx} px
										</div>
									) : null}
									{dpiInfo ? (
										<div>
										<strong>{__('DPI')}:</strong> {Math.round(dpiInfo.dpi)}
										</div>
									) : null}
								</div>
							) : null}
							{quality ? <div className={`khlp__quality khlp__quality--${quality.level}`}>{quality.message}</div> : null}
						</div>

						{canEditImage ? (
							<div className="khlp__editBox">
							<div className="khlp__sectionSub" style={{ marginBottom: 8 }}>{__('Afbeelding bewerken')}</div>
								<div className="khlp__row khlp__row--edit">
									<label className="khlp__label">
									{__('Schaal')}
										<input type="range" min={0.2} max={2.5} step={0.01} value={stickerScale} onChange={(e) => setStickerScale(Number(e.target.value))} />
										<div className="khlp__help">{Math.round(stickerScale * 100)}%</div>
									</label>
									<label className="khlp__label">
									{__('Rotatie')}
										<input type="range" min={-180} max={180} step={1} value={stickerRotation} onChange={(e) => setStickerRotation(Number(e.target.value))} />
										<div className="khlp__help">{Math.round(stickerRotation)}°</div>
									</label>
								</div>
								<label className="khlp__label" style={{ marginTop: 10 }}>
								{__('Positie')}
									<div className="khlp__editActions">
									<button type="button" onClick={() => imageRender && setImgPos({ x: shapeBox.x + (shapeBox.w - imageRender.w) / 2, y: shapeBox.y + (shapeBox.h - imageRender.h) / 2 })}>{__('Centreren')}</button>
										<button type="button" onClick={() => setStickerRotation((r) => normalizeDeg180(Number(r || 0) - 90))}>⟲ 90°</button>
										<button type="button" onClick={() => setStickerRotation((r) => normalizeDeg180(Number(r || 0) + 90))}>⟳ 90°</button>
									<button type="button" onClick={() => { setStickerScale(1); setStickerRotation(0); }}>{__('Reset')}</button>
									</div>
									<div className="khlp__nudgeWrap">
									<div className="khlp__nudgeHelp">{__('Tip: sleep je afbeelding in de preview. Gebruik de pijltjes voor fijne correcties.')}</div>
									<div className="khlp__nudgeGrid" role="group" aria-label={__('Afbeelding verplaatsen')}>
											<span />
										<button type="button" className="khlp__nudgeBtn" onClick={() => nudge(0, -2)} aria-label={__('Omhoog')}>↑</button>
											<span />
										<button type="button" className="khlp__nudgeBtn" onClick={() => nudge(-2, 0)} aria-label={__('Links')}>←</button>
										<button type="button" className="khlp__nudgeBtn" onClick={() => nudge(2, 0)} aria-label={__('Rechts')}>→</button>
										<button type="button" className="khlp__nudgeBtn" onClick={() => nudge(0, 2)} aria-label={__('Omlaag')}>↓</button>
										</div>
									</div>
								</label>
							</div>
						) : (
							<div className="khlp__help" style={{ marginTop: 12 }}>
								{__('Upload een bestand om de afbeelding te kunnen schalen, roteren en verplaatsen.')}
							</div>
						)}
					</div>

					<div className="khlp__section" id="khlp-section-material">
					<div className="khlp__sectionTitle">2. {__('Materiaal')}</div>
					<div className="khlp__sectionSub">{__('Kies je materiaal en eventueel een afwerking.')}</div>
						<div>
							{templateCfg && templateCfg.materials?.length ? (
								<div className="khlp__label">
								{__('Materiaal')}
									<div className="khlp__optionGrid" role="list">
										{templateCfg.materials.map((m) => {
											const isActive = (materialId || templateCfg.defaultMaterialId || 0) === m.id;
											return (
												<button
													key={m.id}
													type="button"
													className={`khlp__optionCard ${isActive ? 'isActive' : ''}`}
													onClick={() => {
														setMaterialTouched(true);
														setMaterialId(m.id);
													}}
												>
													<span className="khlp__optionTitle">{m.title}</span>
													<TooltipIcon content={(m as any).tooltip} />
												</button>
											);
										})}
									</div>
								</div>
							) : null}

							{templateCfg && Array.isArray(templateCfg.finishes) && templateCfg.finishes.length && !hideFinishesForMaterial ? (
								<div className="khlp__label">
								{__('Afwerking')}
									<div className="khlp__optionGrid" role="list">
										{showNoneFinish ? (
											<button
												type="button"
												className={`khlp__optionCard ${(finishId || 0) === 0 ? 'isActive' : ''}`}
												onClick={() => {
													setFinishTouched(true);
													setFinishId(0);
											}}
											>
											<span className="khlp__optionTitle">{noneFinishTitle}</span>
											<TooltipIcon content={noneFinishTooltip} />
										</button>
										) : null}
										{visibleFinishes.map((f) => {
											const isActive = (finishId || 0) === f.id;
											return (
												<button
													key={f.id}
													type="button"
													className={`khlp__optionCard ${isActive ? 'isActive' : ''}`}
													onClick={() => {
														setFinishTouched(true);
														setFinishId(f.id);
													}}
												>
													<span className="khlp__optionTitle">{f.title}</span>
													<TooltipIcon content={f.tooltip} />
												</button>
											);
										})}
									</div>
							<div className="khlp__help">{__('Kies je laminaat/afwerking (optioneel).')}</div>
								</div>
							) : null}

							{/* Leveringsvorm (delivery format) */}
							{deliveryOptions.length ? (
								<div className="khlp__label">
									{__('Levering als')}
									<div className="khlp__optionGrid" role="list">
										{deliveryOptions.map((opt) => {
											const isActive = deliveryKey === opt.key;
											return (
												<button
													key={opt.key}
													type="button"
													className={`khlp__optionCard ${isActive ? 'isActive' : ''}`}
													onClick={() => setDeliveryKey(opt.key)}
												>
													<span className="khlp__optionTitle">{__(opt.title)}</span>
													<TooltipIcon content={__(String(opt.tooltip || ''))} />
												</button>
											);
										})}
									</div>
								</div>
							) : null}
						</div>
					</div>

					<div className="khlp__section" id="khlp-section-shape">
					<div className="khlp__sectionTitle">3. {__('Vorm')}</div>
					<div className="khlp__sectionSub">{__('Kies de snijvorm van je sticker.')}</div>
						<div>
							{shapeMockups.length ? (
								<div className="khlp__label">
								{__('Vorm')}
									<div className="khlp__shapeGrid">
										{shapeMockups.map((m) => {
											const st = String(m.type || '').replace('vorm_', '');
											const isActive = (mockupId || selectedMockup?.id) === m.id;
											return (
												<button
													key={m.id}
													type="button"
													className={`khlp__shapeBtn ${isActive ? 'isActive' : ''}`}
													onClick={() => selectShape(m.id, st)}
												>
													<TooltipIcon content={(m as any).tooltip} corner />
													<svg className="khlp__shapeIcon" viewBox="0 0 48 48" aria-hidden="true">
														{st === 'rond' ? (
															<circle cx="24" cy="24" r="16" fill="none" stroke="#111" strokeWidth="2" />
														) : st === 'ovaal_h' ? (
															<ellipse cx="24" cy="24" rx="18" ry="12" fill="none" stroke="#111" strokeWidth="2" />
														) : st === 'contour' ? (
															<path
																d="M17 10c4-2 9-2 14 1 4 2 6 6 5 10-1 5-5 9-10 11-6 3-12 1-14-3-2-4 0-8 3-10 2-2 2-6 2-9z"
																fill="none"
																stroke="#111"
																strokeWidth="2"
																strokeLinecap="round"
																strokeLinejoin="round"
															/>
														) : st === 'rechthoek_rounded' ? (
															<rect x="8" y="14" width="32" height="20" rx="6" ry="6" fill="none" stroke="#111" strokeWidth="2" />
														) : st === 'rechthoek' ? (
															<rect x="8" y="14" width="32" height="20" fill="none" stroke="#111" strokeWidth="2" />
														) : st === 'vierkant_rounded' ? (
															<rect x="12" y="12" width="24" height="24" rx="6" ry="6" fill="none" stroke="#111" strokeWidth="2" />
														) : (
															<rect x="12" y="12" width="24" height="24" fill="none" stroke="#111" strokeWidth="2" />
														)}
													</svg>
													<div className="khlp__shapeLabel">{m.title}</div>
												</button>
											);
										})}
									</div>
							<div className="khlp__help">{__('Kies de snijvorm: vierkant, rond, rechthoek of ovaal.')}</div>
								</div>
							) : null}
						</div>
					</div>

					<div className="khlp__section" id="khlp-section-size">
					<div className="khlp__sectionTitle">4. {__('Afmeting')}</div>
					<div className="khlp__sectionSub">{__('Vul je gewenste afmetingen in.')}</div>
						<div>
							<div className="khlp__sizeRow">
								<div className="khlp__sizeField">
								<div className="khlp__sizeFieldTitle">{__('Breedte')}</div>
									<div className="khlp__inputGroup">
										<input
											className="khlp__inputSmall"
											type="number"
											step={0.1}
											min={minW}
											max={maxW}
											value={widthInput}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
												lastEditedRef.current = 'w';
												setWidthInput(e.target.value);
											}}
											onBlur={() => commitWidth()}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
												e.preventDefault();
												commitWidth();
											}
										}}
										/>
										<span className="khlp__unit">cm</span>
									</div>
								</div>
								<div className="khlp__times">×</div>
								<div className="khlp__sizeField">
								<div className="khlp__sizeFieldTitle">{__('Hoogte')}</div>
									<div className="khlp__inputGroup">
										<input
											className="khlp__inputSmall"
											type="number"
											step={0.1}
											min={minH}
											max={maxH}
											value={heightInput}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
												lastEditedRef.current = 'h';
												setHeightInput(e.target.value);
											}}
											onBlur={() => commitHeight()}
											onKeyDown={(e) => {
												if (e.key === 'Enter') {
												e.preventDefault();
												commitHeight();
											}
										}}
										/>
										<span className="khlp__unit">cm</span>
									</div>
								</div>
								<div className="khlp__sizeField">
								<div className="khlp__sizeFieldTitle">{__('Aantal')}</div>
									<div className="khlp__inputGroup">
										<input
											id="aantal"
											className="khlp__inputSmall"
											type="number"
											step={1}
											min={cfg.minQty || 1}
											value={qty}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQty(Math.max(cfg.minQty || 1, Number(e.target.value)))}
										/>
									</div>
								</div>
							</div>
								{tubeError ? (
									<div className="khlp__tubeError" role="alert">
										{tubeError}
									</div>
								) : null}
						{tiers.length ? (
							<div className="khlp__help" style={{ marginTop: 10 }}>
								<strong>{__('Staffelkorting')}</strong>
								<ul style={{ margin: '6px 0 0 18px' }}>
									{tiers.map((t, idx) => {
										const min = Number(t.min || 0);
										const p = tierUnitPrices[min];
										const money = p ? formatMoney(p.unitPrice, p.currency) : '—';
										return (
										<li key={idx} className={idx === activeTierIdx ? 'active-staffel' : ''}>
											{__('Vanaf')} {t.min} {__('stuks')}: {money} {__('per stuk')}
											</li>
									);
									})}
								</ul>
								{qty >= minQty ? (
									nextTier && (nextTier.min || 0) > qty ? (
									<div className="khlp__tiersUpsell" style={{ marginTop: 8 }}>
											{upsellTemplate ? (
												<span
													dangerouslySetInnerHTML={{
														__html: renderTemplate(upsellTemplate, {
															remaining: Math.max(0, Number(nextTier.min || 0) - qty),
															nextMin: Number(nextTier.min || 0),
															nextDiscount: Math.round((1 - Number(nextTier.mult)) * 100),
															maxDiscount,
															nextUnitPrice,
															currentUnitPrice,
														}),
													}}
												/>
											) : (
												<span>
													{__('Tip')}: {__('Voeg nog')} {Math.max(0, Number(nextTier.min || 0) - qty)} {__('stickers toe en pak')} {Math.round((1 - Number(nextTier.mult)) * 100)}% {__('korting')}!
												</span>
											)}
										</div>
									) : tiers.length ? (
									<div className="khlp__tiersUpsell" style={{ marginTop: 8 }}>
											{maxTemplate ? (
												<span dangerouslySetInnerHTML={{ __html: renderTemplate(maxTemplate, { maxDiscount }) }} />
											) : (
												<span>{__('Je profiteert nu van de maximale korting!')}</span>
											)}
										</div>
									) : null
								) : null}
							</div>
						) : null}
							<div className="khlp__help">{__('Min')} {minW}×{minH} cm — {__('max')} {maxW}×{maxH} cm</div>
							{/* prijs staat links (sticky) */}
						</div>
					</div>

					<div className="khlp__section" id="khlp-section-order">
					<div className="khlp__sectionTitle">5. {__('Bestellen')}</div>
				<div className="khlp__sectionSub">{__('Controleer je prijs en voeg toe aan je winkelmandje.')}</div>
						<div>
						<div className="khlp__label">
						{__('Productie & levertijd')}
							<div className="khlp__optionGrid khlp__prodGrid" role="list">
								{productionOptions.map((opt) => {
									const isActive = productionKey === opt.key;
									const surcharge = opt.surchargePercent > 0 ? `+${Math.round(opt.surchargePercent)}%` : '';
									return (
										<button
											key={opt.key}
											type="button"
											className={`khlp__optionCard khlp__prodCard ${isActive ? 'isActive' : ''}`}
											onClick={() => setProductionKey(opt.key)}
										>
											<div className="khlp__prodTitle">{opt.title}</div>
											<div className="khlp__prodText">{opt.text}</div>
											<div className="khlp__prodFooter">
												<div className="khlp__prodFooterRight">
													{surcharge ? <span className="khlp__prodSurcharge">{surcharge}</span> : null}
													<TooltipIcon content={opt.tooltip} />
												</div>
											</div>
										</button>
									);
								})}
							</div>
							{hasLamination ? <div className="khlp__note">{laminatedNote}</div> : null}
						</div>

							{/* prijs + winkelwagen knop staan links (sticky) */}
						</div>
					</div>
					<div className="khlp__status">{status}</div>
					{errors.length ? (
						<div className="khlp__errors" role="alert">
							{errors.map((e, i) => (
								<div key={i}>{e}</div>
							))}
						</div>
					) : null}


					</div>
				</div>
			</div>
		</div>
	);
}

const mount = document.getElementById('khl-print-configurator');
if (mount && window.khlPrintCfg) {
	createRoot(mount).render(<App />);
}
